// frontend/src/modules/esign/components/FieldEditor.tsx
//
// Step 3 of the prepare wizard: place signature/initials/date/text/checkbox
// fields on the document, one per active recipient. Palette → click page →
// place at default size; drag to move; corner handle to resize; Delete key
// to remove the selection. Fields are ratio-based (0..1, top-left origin)
// so they render identically regardless of viewport size — same contract
// the signer page (Task 9) and the backend both use.
import * as React from 'react';
import { Trash2 } from 'lucide-react';
import { PdfPageCanvas } from './PdfPageCanvas';
import type { PDFDocumentProxy } from '../pdf';
import type { EsignFieldType } from '../types';
import type { DraftRecipient } from './RecipientsEditor';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';

export interface DraftField {
  localId: string;
  recipientLocalId: string;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  type: EsignFieldType;
  required: boolean;
}

export const DEFAULT_FIELD_SIZE: Record<EsignFieldType, { w: number; h: number }> = {
  signature: { w: 0.22, h: 0.06 },
  initials: { w: 0.08, h: 0.05 },
  date: { w: 0.14, h: 0.04 },
  text: { w: 0.2, h: 0.04 },
  checkbox: { w: 0.03, h: 0.03 },
};

const FIELD_TYPE_LABEL: Record<EsignFieldType, string> = {
  signature: 'Signature',
  initials: 'Initials',
  date: 'Date',
  text: 'Text',
  checkbox: 'Checkbox',
};

const PALETTE_TYPES: EsignFieldType[] = ['signature', 'initials', 'date', 'text', 'checkbox'];

/** 6-color cycle, one fixed accent per signer for the life of the wizard. */
export const RECIPIENT_ACCENTS = ['#E05A44', '#3B6CE0', '#7C4FE0', '#D69A1E', '#1E9E6B', '#D64A8A'];

export function makeFieldId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `field-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), Math.max(min, max));
}

/** Every signer must have at least one `signature` field — mirrors the backend send guard. */
export function everySignerHasSignatureField(recipients: DraftRecipient[], fields: DraftField[]): boolean {
  const signers = recipients.filter((r) => r.role === 'signer');
  if (signers.length === 0) return false;
  return signers.every((s) =>
    fields.some((f) => f.recipientLocalId === s.localId && f.type === 'signature'),
  );
}

interface FieldEditorProps {
  pdf: PDFDocumentProxy;
  pageCount: number;
  recipients: DraftRecipient[];
  fields: DraftField[];
  onFieldsChange: (fields: DraftField[]) => void;
}

export function FieldEditor({ pdf, pageCount, recipients, fields, onFieldsChange }: FieldEditorProps) {
  const signers = React.useMemo(() => recipients.filter((r) => r.role === 'signer'), [recipients]);
  const [activePage, setActivePage] = React.useState(1);
  const [paletteType, setPaletteType] = React.useState<EsignFieldType | null>(null);
  const [activeRecipientLocalId, setActiveRecipientLocalId] = React.useState<string | null>(
    signers[0]?.localId ?? null,
  );
  const [selectedFieldLocalId, setSelectedFieldLocalId] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (activeRecipientLocalId && signers.some((s) => s.localId === activeRecipientLocalId)) return;
    setActiveRecipientLocalId(signers[0]?.localId ?? null);
  }, [signers, activeRecipientLocalId]);

  const accentFor = React.useCallback(
    (recipientLocalId: string): string => {
      const idx = signers.findIndex((s) => s.localId === recipientLocalId);
      return RECIPIENT_ACCENTS[Math.max(0, idx) % RECIPIENT_ACCENTS.length];
    },
    [signers],
  );

  function updateField(localId: string, patch: Partial<DraftField>) {
    onFieldsChange(fields.map((f) => (f.localId === localId ? { ...f, ...patch } : f)));
  }

  function removeField(localId: string) {
    onFieldsChange(fields.filter((f) => f.localId !== localId));
    setSelectedFieldLocalId((cur) => (cur === localId ? null : cur));
  }

  function handleContainerClick(e: React.MouseEvent<HTMLDivElement>) {
    // Only place a field when the click landed on the page background, not
    // on an existing field box or its resize handle (those stopPropagation
    // on their own click, but guard anyway for safety).
    //
    // The background is either the container itself, or the <canvas> that
    // PdfPageCanvas paints on top of it (normally pointer-events:none, so
    // clicks land on the container directly — but this is a belt-and-braces
    // check in case that ever changes, and it's what real-browser clicks hit
    // before that fix).
    const target = e.target as HTMLElement;
    const isBackground = target === e.currentTarget || target.tagName === 'CANVAS';
    if (!isBackground) return;
    if (!paletteType || !activeRecipientLocalId) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;

    const size = DEFAULT_FIELD_SIZE[paletteType];
    const rawX = (e.clientX - rect.left) / rect.width - size.w / 2;
    const rawY = (e.clientY - rect.top) / rect.height - size.h / 2;
    const x = clamp(rawX, 0, 1 - size.w);
    const y = clamp(rawY, 0, 1 - size.h);

    const newField: DraftField = {
      localId: makeFieldId(),
      recipientLocalId: activeRecipientLocalId,
      page: activePage,
      x,
      y,
      w: size.w,
      h: size.h,
      type: paletteType,
      required: true,
    };
    onFieldsChange([...fields, newField]);
    setSelectedFieldLocalId(newField.localId);
  }

  // Delete/Backspace removes the selected field, unless focus is in a text input.
  React.useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!selectedFieldLocalId) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      e.preventDefault();
      removeField(selectedFieldLocalId);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldLocalId, fields]);

  const fieldsOnPage = fields.filter((f) => f.page === activePage);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1.5">
          {signers.length === 0 ? (
            <span className="text-[13px] text-ink/50 dark:text-white/50">Add a signer first.</span>
          ) : (
            signers.map((s) => (
              <button
                key={s.localId}
                type="button"
                onClick={() => setActiveRecipientLocalId(s.localId)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium ring-1 transition-colors',
                  activeRecipientLocalId === s.localId
                    ? 'ring-ink/20 dark:ring-white/30 bg-white dark:bg-white/10'
                    : 'ring-ink/8 dark:ring-white/10 bg-white/50 dark:bg-white/[0.03] opacity-70',
                )}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: accentFor(s.localId) }} />
                {s.name || s.email || 'Signer'}
              </button>
            ))
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {PALETTE_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              disabled={!activeRecipientLocalId}
              onClick={() => setPaletteType((cur) => (cur === t ? null : t))}
              className={cn(
                'rounded-full px-3 py-1.5 text-[12.5px] font-medium ring-1 transition-colors disabled:opacity-40',
                paletteType === t
                  ? 'bg-coral text-white ring-coral'
                  : 'bg-white/70 dark:bg-white/[0.04] ring-ink/8 dark:ring-white/10 text-ink dark:text-white',
              )}
            >
              {FIELD_TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        {pageCount > 1 && (
          <div className="flex items-center gap-2 ml-auto text-[13px]">
            <Button type="button" variant="outline" size="sm" disabled={activePage <= 1} onClick={() => setActivePage((p) => p - 1)}>
              Prev
            </Button>
            <span className="tabular-nums">Page {activePage} of {pageCount}</span>
            <Button type="button" variant="outline" size="sm" disabled={activePage >= pageCount} onClick={() => setActivePage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-auto rounded-2xl bg-ink/5 dark:bg-black/20 p-4">
        <PdfPageCanvas
          ref={containerRef}
          pdf={pdf}
          pageNumber={activePage}
          width={640}
          onClick={handleContainerClick}
          data-testid="field-editor-page"
          style={{ cursor: paletteType ? 'crosshair' : 'default' }}
        >
          {fieldsOnPage.map((f) => (
            <FieldBox
              key={f.localId}
              field={f}
              accentColor={accentFor(f.recipientLocalId)}
              selected={f.localId === selectedFieldLocalId}
              containerRef={containerRef}
              onSelect={() => setSelectedFieldLocalId(f.localId)}
              onMove={(x, y) => updateField(f.localId, { x, y })}
              onResize={(w, h) => updateField(f.localId, { w, h })}
              onDelete={() => removeField(f.localId)}
            />
          ))}
        </PdfPageCanvas>
      </div>
    </div>
  );
}

interface FieldBoxProps {
  field: DraftField;
  accentColor: string;
  selected: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
  onResize: (w: number, h: number) => void;
  onDelete: () => void;
}

function FieldBox({ field, accentColor, selected, containerRef, onSelect, onMove, onResize, onDelete }: FieldBoxProps) {
  const dragState = React.useRef<{ pointerOffsetX: number; pointerOffsetY: number; rectW: number; rectH: number } | null>(null);
  const resizeState = React.useRef<{ rectLeft: number; rectTop: number; rectW: number; rectH: number } | null>(null);

  function handleDragPointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    onSelect();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const fieldLeftPx = rect.left + field.x * rect.width;
    const fieldTopPx = rect.top + field.y * rect.height;
    dragState.current = {
      pointerOffsetX: e.clientX - fieldLeftPx,
      pointerOffsetY: e.clientY - fieldTopPx,
      rectW: rect.width,
      rectH: rect.height,
    };
    window.addEventListener('pointermove', handleDragPointerMove);
    window.addEventListener('pointerup', handleDragPointerUp);
  }

  function handleDragPointerMove(e: PointerEvent) {
    const s = dragState.current;
    if (!s) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const left = rect?.left ?? 0;
    const top = rect?.top ?? 0;
    const nx = clamp((e.clientX - s.pointerOffsetX - left) / s.rectW, 0, 1 - field.w);
    const ny = clamp((e.clientY - s.pointerOffsetY - top) / s.rectH, 0, 1 - field.h);
    onMove(nx, ny);
  }

  function handleDragPointerUp() {
    dragState.current = null;
    window.removeEventListener('pointermove', handleDragPointerMove);
    window.removeEventListener('pointerup', handleDragPointerUp);
  }

  function handleResizePointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    onSelect();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    resizeState.current = { rectLeft: rect.left, rectTop: rect.top, rectW: rect.width, rectH: rect.height };
    window.addEventListener('pointermove', handleResizePointerMove);
    window.addEventListener('pointerup', handleResizePointerUp);
  }

  function handleResizePointerMove(e: PointerEvent) {
    const s = resizeState.current;
    if (!s) return;
    const w = clamp((e.clientX - s.rectLeft) / s.rectW - field.x, 0.02, 1 - field.x);
    const h = clamp((e.clientY - s.rectTop) / s.rectH - field.y, 0.02, 1 - field.y);
    onResize(w, h);
  }

  function handleResizePointerUp() {
    resizeState.current = null;
    window.removeEventListener('pointermove', handleResizePointerMove);
    window.removeEventListener('pointerup', handleResizePointerUp);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${field.type} field`}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onPointerDown={handleDragPointerDown}
      style={{
        position: 'absolute',
        left: `${field.x * 100}%`,
        top: `${field.y * 100}%`,
        width: `${field.w * 100}%`,
        height: `${field.h * 100}%`,
        background: `${accentColor}33`,
        border: `1.5px solid ${accentColor}`,
        borderRadius: 4,
        cursor: 'move',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: selected ? `2px solid ${accentColor}` : 'none',
        outlineOffset: 1,
      }}
    >
      <span className="text-[10px] font-medium truncate px-0.5 pointer-events-none" style={{ color: accentColor }}>
        {FIELD_TYPE_LABEL[field.type]}
      </span>

      {selected && (
        <>
          <button
            type="button"
            aria-label="Delete field"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{ position: 'absolute', top: -10, right: -10, background: accentColor }}
            className="h-5 w-5 rounded-full text-white flex items-center justify-center"
          >
            <Trash2 className="h-3 w-3" />
          </button>
          <div
            role="presentation"
            aria-label="Resize field"
            onPointerDown={handleResizePointerDown}
            style={{
              position: 'absolute',
              bottom: -5,
              right: -5,
              width: 10,
              height: 10,
              borderRadius: 9999,
              background: accentColor,
              cursor: 'nwse-resize',
            }}
          />
        </>
      )}
    </div>
  );
}
